/**
 * Catalog (PhysicalStoreIngestionService) CrossFrame Listeners
 *
 * Handles CrossFrame requests for Catalog operations.
 * Validates required parameters and delegates to the gateway client via getServiceClient().
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'

import {
    GetTemplatePresignedUrlInput,
    GetTemplatePresignedUrlOutput,
    PutJwoProductsInput,
    PutJwoProductsOutput,
    PutJwoProductInput,
    PutJwoProductOutput,
    ArchiveJwoProductsInput,
    ArchiveJwoProductsOutput,
    PutEcoProductsInput,
    PutEcoProductsOutput,
    ListStatusInput,
    ListStatusOutput,
    GetStatusInput,
    GetStatusOutput,
    ListJwoProductsInput,
    ListJwoProductsOutput,
    ListCatalogMerchantsInput,
    ListCatalogMerchantsOutput,
} from '@amzn/coral_com-amazon-jwostoremanagementgateway'
import { validateRequired, validateRequiredArray } from './listenerUtils'

// ========================================
// READ OPERATIONS
// ========================================

/**
 * Get a presigned S3 URL for downloading the catalog template.
 * Required: merchantId, marketplaceId
 */
export async function getTemplatePresignedUrlListener (message: any): Promise<GetTemplatePresignedUrlOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'marketplaceId')
    const client = getServiceClient()
    return client.getTemplatePresignedUrl(GetTemplatePresignedUrlInput.clone(params)).toPromise()
}

/**
 * List feed ingestion statuses with pagination.
 * Required: merchantId
 */
export async function listStatusListener (message: any): Promise<ListStatusOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId')
    const client = getServiceClient()
    return client.listStatus(ListStatusInput.clone(params)).toPromise()
}

/**
 * Get a single ingestion status with optional report URL.
 * Required: merchantId, ingestionId
 */
export async function getStatusListener (message: any): Promise<GetStatusOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'ingestionId')
    const client = getServiceClient()
    return client.getStatus(GetStatusInput.clone(params)).toPromise()
}

/**
 * List the merchant's product catalog, paginated by maxResults and an opaque nextToken.
 * Required: merchantId, marketplaceId
 *
 * marketplaceId is validated here because the Gateway needs it for the Mons marketplace
 * header; omitting it would otherwise surface as a Gateway validation error after a round trip.
 */
export async function listJwoProductsListener (message: any): Promise<ListJwoProductsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'marketplaceId')
    const client = getServiceClient()
    return client.listJwoProducts(ListJwoProductsInput.clone(params)).toPromise()
}

/**
 * List the merchants the caller may use with the catalog operations, one page at a time.
 * The Gateway derives the list from the caller's authorized scope (the organization's merchants,
 * or the legacy instance's merchant mappings). Only the paging fields are forwarded; anything
 * else the tool sends is dropped, so it cannot widen the scope.
 */
export async function listCatalogMerchantsListener (message: any): Promise<ListCatalogMerchantsOutput> {
    const params = (message && message.data) || {}
    const paging: Record<string, unknown> = {}
    if (params.maxResults !== undefined) paging.maxResults = params.maxResults
    if (params.nextToken !== undefined) paging.nextToken = params.nextToken
    const client = getServiceClient()
    return client.listCatalogMerchants(ListCatalogMerchantsInput.clone(paging)).toPromise()
}

// ========================================
// WRITE OPERATIONS
// ========================================

/**
 * Bulk product upload via presigned URL.
 * Required: merchantId, uploadUrl
 */
export async function putJwoProductsListener (message: any): Promise<PutJwoProductsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'uploadUrl')
    const client = getServiceClient()
    return client.putJwoProducts(PutJwoProductsInput.clone(params)).toPromise()
}

/**
 * Single product create/update.
 * Required: merchantId, itemSku, externalProductId, externalProductIdType
 */
export async function putJwoProductListener (message: any): Promise<PutJwoProductOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'itemSku', 'externalProductId', 'externalProductIdType')
    const client = getServiceClient()
    return client.putJwoProduct(PutJwoProductInput.clone(params)).toPromise()
}

/**
 * Archive products by SKU list.
 * Required: merchantId, skuList (non-empty array)
 */
export async function archiveJwoProductsListener (message: any): Promise<ArchiveJwoProductsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId')
    validateRequiredArray(params, 'skuList')
    const client = getServiceClient()
    return client.archiveJwoProducts(ArchiveJwoProductsInput.clone(params)).toPromise()
}

/**
 * ECO product bulk upload via presigned URL.
 * Required: merchantId, uploadUrl
 */
export async function putEcoProductsListener (message: any): Promise<PutEcoProductsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'uploadUrl')
    const client = getServiceClient()
    return client.putEcoProducts(PutEcoProductsInput.clone(params)).toPromise()
}
